// frontend/src/components/notifications/NotificationBell.js
import React, { useState } from 'react';
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  ListItemIcon,
  ListItemText,
  alpha
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventIcon from '@mui/icons-material/Event';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useNotifications } from '../../context/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

const NotificationBell = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearRead } = useNotifications();
  const [anchorEl, setAnchorEl] = useState(null);
  
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleNotificationClick = (notification) => {
    // Mark as read (which removes it from the unread-only list)
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Navigate based on notification type and related data
    if (notification.related_type === 'appointment' && notification.related_id) {
      navigate(`/calendar?event=${notification.related_id}`);
    } else if (notification.related_type === 'event' && notification.related_id) {
      navigate(`/calendar?event=${notification.related_id}`);
    } else if (notification.related_type === 'report' && notification.related_id) {
      navigate(`/reports/${notification.related_id}`);
    } else if (notification.type === 'version_update' || notification.related_type === 'changelog') {
      navigate(`/changelog`);
    }

    handleClose();
  };
  
  const getIconByType = (type) => {
    switch (type) {
      case 'event_assigned':
      case 'event_updated':
      case 'event_deleted':
        return <EventIcon fontSize="small" sx={{ color: theme.palette.info.main }} />;
      case 'report_submitted':
      case 'report_required':
        return <AssignmentIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />;
      case 'report_follow_up':
        return <ErrorIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />;
      case 'completion':
        return <CheckCircleIcon fontSize="small" sx={{ color: theme.palette.success.main }} />;
      case 'version_update':
        return <NewReleasesIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />;
      default:
        return <NotificationsIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />;
    }
  };
  
  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { 
        addSuffix: true,
        locale: de
      });
    } catch (e) {
      return '';
    }
  };
  
  return (
    <>
      <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
        <IconButton 
          color="inherit" 
          onClick={handleOpen}
          sx={{ 
            background: unreadCount > 0 ? alpha(theme.palette.common.white, 0.1) : 'transparent',
            '&:hover': {
              background: alpha(theme.palette.common.white, 0.2),
            }
          }}
        >
          <Badge 
            badgeContent={unreadCount} 
            color="error"
            overlap="circular"
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </motion.div>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            maxHeight: 400,
            width: 'min(340px, calc(100vw - 32px))',
            borderRadius: 2,
            mt: 1,
            overflow: 'hidden',
            '& .MuiMenuItem-root': {
              py: 1.5,
              whiteSpace: 'normal',
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, bgcolor: theme.palette.primary.main, color: 'white' }}>
          <Typography variant="h6">
            Benachrichtigungen
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {unreadCount} ungelesene {unreadCount === 1 ? 'Mitteilung' : 'Mitteilungen'}
          </Typography>
        </Box>
        
        <Box 
          sx={{ 
            maxHeight: 300, 
            overflowY: 'auto',
            bgcolor: theme.palette.background.paper,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              borderRadius: '4px',
            },
          }}
        >
          {notifications.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Keine Benachrichtigungen
              </Typography>
            </Box>
          )}
          
          {notifications.map((notification) => (
            <MenuItem 
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                px: 2,
                py: 1.5,
                borderLeft: notification.read ? 'none' : `4px solid ${theme.palette.primary.main}`,
                bgcolor: notification.read ? 'transparent' : alpha(theme.palette.primary.light, 0.1),
              }}
            >
              <ListItemIcon>
                {getIconByType(notification.type)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: notification.read ? 'normal' : 'bold',
                      color: theme.palette.text.primary
                    }}
                  >
                    {notification.title}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', display: 'block' }}>
                      {notification.message}
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {formatTime(notification.created_at)}
                    </Typography>
                  </>
                }
                secondaryTypographyProps={{ component: 'div' }}
              />
            </MenuItem>
          ))}
        </Box>
        
        <Divider />
        
        {notifications.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
            <Button
              onClick={async () => {
                await markAllAsRead();
                await clearRead();
                handleClose();
              }}
              size="small"
              sx={{ fontWeight: 'normal', textTransform: 'none' }}
            >
              Alle löschen
            </Button>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;